import {
  Children,
  isValidElement,
  type KeyboardEvent,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cx } from '../lib/cx';
import styles from './Field.module.css';
import { Icon } from './Icon';

type Item = { value: string; label: string; group: string | null; disabled: boolean };

/** Plain text of an option's children ("Cibo", or a label built from several pieces). */
function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

/** Reads the same <option> and <optgroup> children a native <select> takes. */
function readItems(children: ReactNode, group: string | null = null): Item[] {
  const items: Item[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    const element = child as ReactElement<{
      value?: string;
      label?: string;
      disabled?: boolean;
      children?: ReactNode;
    }>;
    if (element.type === 'option') {
      const label = textOf(element.props.children);
      items.push({
        value: element.props.value ?? label,
        label,
        group,
        disabled: Boolean(element.props.disabled),
      });
    } else if (element.type === 'optgroup') {
      items.push(...readItems(element.props.children, element.props.label ?? null));
    } else {
      items.push(...readItems(element.props.children, group));
    }
  });
  return items;
}

/** Long lists get a search box. */
const SEARCH_FROM = 9;
const GAP = 6;
const MARGIN = 8;
const MAX_HEIGHT = 380;
const MIN_HEIGHT = 120;
/** Below the field is where a list is expected: it opens there whenever this much fits. */
const ENOUGH_BELOW = 240;

export type SelectProps = {
  id: string;
  describedBy?: string;
  invalid?: boolean;
  name?: string;
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  children: ReactNode;
};

/**
 * A select drawn by us: the list opens in the top layer (above dialogs), stays inside the
 * window (it opens upwards when there is no room below, and scrolls), can be searched, and works
 * with the keyboard like the native one. The value travels in a hidden input, so forms read it.
 */
export function Select({
  id,
  describedBy,
  invalid,
  name,
  value,
  defaultValue,
  onValueChange,
  disabled,
  children,
}: SelectProps) {
  const items = useMemo(() => readItems(children), [children]);
  const firstEnabled = items.find((item) => !item.disabled)?.value ?? '';
  const [own, setOwn] = useState(defaultValue ?? firstEnabled);
  const selected = value ?? own;
  const current = items.find((item) => item.value === selected);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(-1);
  const listId = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const popover = useRef<HTMLDivElement>(null);
  const search = useRef<HTMLInputElement>(null);
  const typed = useRef({ text: '', at: 0 });
  const side = useRef<'top' | 'bottom' | null>(null);
  const searchable = items.length >= SEARCH_FROM;

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      `${item.label} ${item.group ?? ''}`.toLowerCase().includes(needle),
    );
  }, [items, query]);

  const place = useCallback(() => {
    const button = trigger.current;
    const panel = popover.current;
    if (!button || !panel) return;
    const rect = button.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const below = viewportHeight - rect.bottom - GAP - MARGIN;
    const above = rect.top - GAP - MARGIN;
    // Measuring without the height limit would send the list back to its top: keep where it was.
    const list = panel.querySelector<HTMLElement>('[role="listbox"]');
    const scrolled = list?.scrollTop ?? 0;
    panel.style.maxHeight = '';
    const natural = Math.min(panel.scrollHeight, MAX_HEIGHT);
    // The side is chosen when the list opens and kept while searching, unless it stops fitting.
    if (side.current === null || (side.current === 'top' ? above : below) < MIN_HEIGHT) {
      side.current = below < Math.min(natural, ENOUGH_BELOW) && above > below ? 'top' : 'bottom';
    }
    const upwards = side.current === 'top';
    const width = Math.min(Math.max(rect.width, 240), viewportWidth - 2 * MARGIN);
    const left = Math.min(Math.max(rect.left, MARGIN), viewportWidth - width - MARGIN);
    panel.style.width = `${width}px`;
    panel.style.left = `${left}px`;
    panel.style.maxHeight = `${Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, upwards ? above : below))}px`;
    if (upwards) {
      panel.style.top = 'auto';
      panel.style.bottom = `${viewportHeight - rect.top + GAP}px`;
    } else {
      panel.style.bottom = 'auto';
      panel.style.top = `${rect.bottom + GAP}px`;
    }
    panel.dataset.side = upwards ? 'top' : 'bottom';
    if (list) list.scrollTop = scrolled;
  }, []);

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) trigger.current?.focus();
  }, []);

  const choose = (item: Item | undefined) => {
    if (!item || item.disabled) return;
    if (value === undefined) setOwn(item.value);
    if (item.value !== selected) onValueChange?.(item.value);
    close();
  };

  const openList = () => {
    if (disabled) return;
    side.current = null;
    setActive(
      Math.max(
        0,
        items.findIndex((item) => item.value === selected),
      ),
    );
    setOpen(true);
  };

  // Show the panel in the top layer and keep it next to the field while the page moves.
  useLayoutEffect(() => {
    const panel = popover.current;
    if (!open || !panel) return;
    if (typeof panel.showPopover === 'function' && !panel.matches(':popover-open')) {
      panel.showPopover();
    }
    place();
    if (searchable) search.current?.focus();
    const onResize = () => place();
    // Only the page moving matters: scrolling the list itself must not re-place it.
    const onScroll = (event: Event) => {
      if (!panel.contains(event.target as Node)) place();
    };
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!panel.contains(target) && !trigger.current?.contains(target)) close(false);
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onScroll, true);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onScroll, true);
      document.removeEventListener('pointerdown', onPointerDown, true);
      if (typeof panel.hidePopover === 'function' && panel.matches(':popover-open')) {
        panel.hidePopover();
      }
    };
  }, [open, place, searchable, close]);

  // The list changes height while searching: stay in place.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-place when the filtered list changes size
  useLayoutEffect(() => {
    if (open) place();
  }, [open, visible.length, place]);

  const reveal = useCallback((index: number) => {
    popover.current
      ?.querySelector<HTMLElement>(`[data-index="${index}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, []);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (open && active >= 0) reveal(active);
  }, [open, active, reveal]);

  /** Highlights an option and shows it, also when it was already highlighted but scrolled away. */
  const highlight = (index: number) => {
    setActive(index);
    reveal(index);
  };

  const move = (step: number) => {
    if (visible.length === 0) return;
    let next = active;
    for (let i = 0; i < visible.length; i++) {
      next = (next + step + visible.length) % visible.length;
      if (!visible[next]?.disabled) break;
    }
    highlight(next);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    if (!open) {
      if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter' || key === ' ') {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (key === 'Escape') {
      // Close the list only, not the dialog around it.
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (key === 'ArrowDown' || key === 'ArrowUp') {
      event.preventDefault();
      move(key === 'ArrowDown' ? 1 : -1);
    } else if (key === 'Home' || key === 'End') {
      event.preventDefault();
      highlight(key === 'Home' ? 0 : visible.length - 1);
    } else if (key === 'Enter') {
      event.preventDefault();
      choose(visible[active]);
    } else if (key === 'Tab') {
      close(false);
    } else if (!searchable && key.length === 1 && !event.metaKey && !event.ctrlKey) {
      // Type-ahead: jump to the first option starting with what was typed.
      const now = Date.now();
      typed.current = {
        text: (now - typed.current.at < 700 ? typed.current.text : '') + key.toLowerCase(),
        at: now,
      };
      const found = visible.findIndex(
        (item) => !item.disabled && item.label.toLowerCase().startsWith(typed.current.text),
      );
      if (found >= 0) setActive(found);
    }
  };

  let lastGroup: string | null = null;
  return (
    <>
      <button
        id={id}
        ref={trigger}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        aria-activedescendant={open && active >= 0 ? `${listId}-${active}` : undefined}
        disabled={disabled}
        className={cx(styles.control, styles.trigger, open && styles.triggerOpen)}
        onClick={() => (open ? close() : openList())}
        onKeyDown={onKeyDown}
      >
        <span className={cx(styles.triggerText, !current && styles.triggerEmpty)}>
          {current ? current.label : 'Scegli…'}
        </span>
        <span className={styles.chevron} aria-hidden="true" />
      </button>
      {name ? <input type="hidden" name={name} value={selected} readOnly /> : null}
      {open ? (
        <div ref={popover} popover="manual" className={styles.popover}>
          {searchable ? (
            <input
              ref={search}
              className={styles.search}
              type="search"
              placeholder="Cerca…"
              aria-label="Cerca nell'elenco"
              aria-controls={listId}
              aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={onKeyDown}
            />
          ) : null}
          <div id={listId} role="listbox" className={styles.list} tabIndex={-1}>
            {visible.length === 0 ? <p className={styles.noResults}>Nessun risultato.</p> : null}
            {visible.map((item, index) => {
              const header =
                item.group !== lastGroup && item.group !== null ? (
                  <div key={`group-${item.group}`} className={styles.group} role="presentation">
                    {item.group}
                  </div>
                ) : null;
              lastGroup = item.group;
              const isSelected = item.value === selected;
              return [
                header,
                <div
                  key={`${item.group ?? ''}:${item.value}`}
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={item.disabled || undefined}
                  tabIndex={-1}
                  className={cx(
                    styles.option,
                    item.group !== null && styles.optionNested,
                    index === active && styles.optionActive,
                    isSelected && styles.optionSelected,
                  )}
                  onPointerEnter={() => setActive(index)}
                  onClick={() => choose(item)}
                  onKeyDown={onKeyDown}
                >
                  <span className={styles.optionLabel}>{item.label}</span>
                  {isSelected ? <Icon name="check" className={styles.optionCheck} /> : null}
                </div>,
              ];
            })}
          </div>
        </div>
      ) : null}
    </>
  );
}
